import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { O2dTableColumn } from '../types/table-column.type.js';
import { readAccessorValue } from './column-accessor.js';

/**
 * Reactive controller that provides the table's single global filter.
 * A row is kept when ANY column's `filterValue` accessor (lower-cased) contains
 * the (lower-cased) needle — an OR-match across columns. Pure/synchronous, no RxJS.
 */
export class TableFilterController implements ReactiveController {
    constructor(host: ReactiveControllerHost) {
        host.addController(this);
    }

    /**
     * No teardown required — this controller holds no timers or subscriptions.
     * Present to satisfy the `ReactiveController` contract (matches the
     * popover-list controller precedent).
     */
    hostDisconnected(): void {
        // intentionally empty: stateless controller, nothing to tear down
    }

    /**
     * Returns the rows whose any column value contains the filter string
     * (case-insensitive substring). An empty filter returns all rows unchanged.
     */
    filter(rows: unknown[], filterStr: string, columns: O2dTableColumn[]): unknown[] {
        if (!filterStr) {
            return rows;
        }

        const needle = filterStr.toLowerCase();
        return rows.filter(row =>
            columns.some(column =>
                String(readAccessorValue(column.filterValue, row)).toLowerCase().includes(needle)
            )
        );
    }
}
